// frontend/src/components/cohesion/onboardingOrchestrator.ts
// UX-01 (2026-06 UX audit) — single-slot onboarding orchestrator.
//
// Problem: on a fresh login the welcome tour, a per-surface tour and the
// "Complete your profile" modal could all fire at the same time and overlap.
// This module is a tiny module-level queue: every onboarding UI claims a
// slot with a priority; only ONE claimant is "active" at any moment and the
// rest stay hidden until the active one releases.
//
// Rules:
// - Same-tick claims are resolved together (microtask) so priority wins
//   regardless of React effect ordering: welcome tour (3) > surface tour (2)
//   > profile-completion modal (1).
// - Activation is NON-preemptive: once a claimant is active it keeps the
//   slot until it releases, even if a higher-priority claim arrives later
//   (never yank a UI out from under the user).
// - Components that need async work before they know whether they will show
//   (e.g. tours fetching /users/me/ui-preferences) claim a "hold" while
//   pending and release if they decide not to run.

import { useEffect, useSyncExternalStore } from 'react'

interface Claim {
  id: string
  priority: number
  seq: number
}

let claims: Claim[] = []
let current: string | null = null
let seqCounter = 0
let scheduled = false
const listeners = new Set<() => void>()

function recompute() {
  scheduled = false
  if (current && !claims.some((c) => c.id === current)) current = null
  if (!current && claims.length > 0) {
    current = [...claims]
      .sort((a, b) => b.priority - a.priority || a.seq - b.seq)[0].id
  }
  listeners.forEach((l) => l())
}

function schedule() {
  if (scheduled) return
  scheduled = true
  queueMicrotask(recompute)
}

export function claimOnboardingSlot(id: string, priority: number): void {
  if (!claims.some((c) => c.id === id)) {
    claims.push({ id, priority, seq: seqCounter++ })
    schedule()
  }
}

export function releaseOnboardingSlot(id: string): void {
  const had = claims.some((c) => c.id === id)
  claims = claims.filter((c) => c.id !== id)
  if (had || current === id) schedule()
}

export function activeOnboardingId(): string | null {
  return current
}

export function subscribeOnboarding(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

const getSnapshot = () => current

/** Test-only: clear all module state between test cases. */
export function _resetOnboardingOrchestrator(): void {
  claims = []
  current = null
  seqCounter = 0
  scheduled = false
  listeners.clear()
}

/**
 * Claim an onboarding slot while `wants` is true; returns whether this
 * claimant is the single active onboarding UI and may render.
 */
export function useOnboardingSlot(id: string, wants: boolean, priority: number): boolean {
  useEffect(() => {
    if (!wants) {
      releaseOnboardingSlot(id)
      return undefined
    }
    claimOnboardingSlot(id, priority)
    return () => releaseOnboardingSlot(id)
  }, [id, wants, priority])

  const active = useSyncExternalStore(subscribeOnboarding, getSnapshot, getSnapshot)
  return wants && active === id
}
